/**
 * Shared, pure helpers for the asset-form Select field renderers
 * (SelectField / RadioField / CheckboxField). Kept framework-free so the
 * label / ordering / selection-resolution logic can be unit-tested in isolation.
 */

/** A single selectable choice. `value` is what is stored/sent; `key` is a display
 *  label used only in key-value mode. `is_default` floats the choice to the top. */
export interface Choice {
  value: string
  key?: string
  is_default?: boolean
}

/** Max choices rendered inline for RADIO/CHECKBOX before the "View All" popover takes over. */
export const SELECT_FIELD_INLINE_CHOICE_LIMIT = 5

/** In key-value mode the display label is the `key` (when present); otherwise the value. */
export function getChoiceLabel(choice: Choice, isKeyValue: boolean): string {
  return isKeyValue && choice.key ? choice.key : choice.value
}

/** Return a copy with default choices floated to the top (display-only ordering).
 *  Relative order within the default and non-default groups is preserved. */
export function withDefaultsFirst(choices: Choice[]): Choice[] {
  return [...choices.filter((c) => c.is_default), ...choices.filter((c) => !c.is_default)]
}

/** Resolve a stored single-select value to its live choice. A stored value that
 *  matches no live choice (removed/edited — "stale") resolves to `null`, so the
 *  control renders nothing selected. The stored value itself is never mutated. */
export function getSelectedChoice(choices: Choice[], value: string | null | undefined): Choice | null {
  if (value == null) return null
  return choices.find((c) => c.value === value) || null
}

/** Resolve stored multi-select values to their live choices, preserving stored order
 *  and dropping stale values (those matching no live choice). */
export function getSelectedChoices(choices: Choice[], values: string[] | null | undefined): Choice[] {
  if (!Array.isArray(values)) return []
  return values
    .map((v) => choices.find((c) => c.value === v))
    .filter((c): c is Choice => !!c)
}
